import { designerServiceStores } from "@intrasync/designer";
import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Estimating Module Service Stores
 *
 * The manhole/designer service stores are sourced directly from the
 * `@intrasync/designer` package (`designerServiceStores`) so they stay in
 * sync automatically whenever the package is upgraded — see the package's
 * `config/serviceStores.ts` for the authoritative list.
 *
 * A handful of generic, cross-cutting stores that `designerServiceStores`
 * lists are intentionally filtered out here because other modules register
 * them with richer configuration (e.g. `serviceView`/summary cards) or the
 * RootStore registers them as built-ins. ServiceStore registration is
 * idempotent (first registration wins — see StoreRegistry.registerServiceStore),
 * so excluding them here keeps the authoritative config from being shadowed.
 */
const STORES_OWNED_BY_OTHER_MODULES = new Set<string>([
  "products", // manufacturing.stores.ts (serviceView + summary cards)
  "customers", // crm.stores.ts (serviceView + summary cards)
  "sales-orders", // crm.stores.ts
  "user", // registered as a built-in by RootStore
]);

/**
 * Estimating-only stores that the designer package does not (yet) declare.
 */
const estimatingOnlyStores: ServiceStoreConfig[] = [
  {
    name: "manhole-validations",
    options: {
      enableStorage: false,
      enableErrorNotifications: false,
      idField: "validationId",
    },
  },
  {
    name: "manhole-forms",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "formId",
    },
  },
  {
    name: "purchase-order-line-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      idField: "formId",
    },
  },
];

/**
 * Per-store option overrides on top of the package config. The
 * manhole-submittal-packages service is compile-only (its API registers no
 * `find`), so the boot-time store hydration always errors — the package config
 * still ships `enableErrorNotifications: true`, which surfaces a spurious
 * error toast on every app start. Silence just the notification host-side.
 */
const STORE_OPTION_OVERRIDES: Record<string, Partial<ServiceStoreConfig["options"]>> = {
  "manhole-submittal-packages": { enableErrorNotifications: false },
};

export const estimatingStores: ServiceStoreConfig[] = [
  ...(designerServiceStores as unknown as ServiceStoreConfig[])
    .filter((store) => !STORES_OWNED_BY_OTHER_MODULES.has(store.name))
    .map((store) =>
      STORE_OPTION_OVERRIDES[store.name]
        ? { ...store, options: { ...store.options, ...STORE_OPTION_OVERRIDES[store.name] } }
        : store,
    ),
  ...estimatingOnlyStores,
];
